import React, { useEffect, useRef, useState } from "react";
import cx from "classnames";

export const STAGGER_EXIT_MS = 200;

export interface StaggerTextRevealLine {
  text: string;
  emphasis?: boolean;
}

interface StaggerTextRevealProps {
  lines: StaggerTextRevealLine[];
  visible: boolean;
  className?: string;
  lineClassName?: string;
  autoDismissMs?: number;
  onHidden?: () => void;
  role?: React.AriaRole;
  "aria-live"?: "off" | "polite" | "assertive";
}

const StaggerTextReveal = ({
  lines,
  visible,
  className,
  lineClassName,
  autoDismissMs,
  onHidden,
  role = "status",
  "aria-live": ariaLive = "polite",
}: StaggerTextRevealProps) => {
  const [isShown, setIsShown] = useState(false);
  const [isHiding, setIsHiding] = useState(false);
  const onHiddenRef = useRef(onHidden);

  useEffect(() => {
    onHiddenRef.current = onHidden;
  }, [onHidden]);

  useEffect(() => {
    if (!visible) {
      setIsShown(false);
      setIsHiding(false);
      return;
    }

    setIsHiding(false);
    setIsShown(false);

    let raf2 = 0;
    const raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => setIsShown(true));
    });

    return () => {
      window.cancelAnimationFrame(raf1);
      if (raf2) {
        window.cancelAnimationFrame(raf2);
      }
    };
  }, [visible]);

  useEffect(() => {
    if (!visible || !isShown || !autoDismissMs) {
      return;
    }

    const dismissTimerId = window.setTimeout(() => {
      setIsHiding(true);
      setIsShown(false);
    }, autoDismissMs);

    return () => window.clearTimeout(dismissTimerId);
  }, [visible, isShown, autoDismissMs]);

  useEffect(() => {
    if (!isHiding) {
      return;
    }

    const exitTimerId = window.setTimeout(() => {
      onHiddenRef.current?.();
    }, STAGGER_EXIT_MS);

    return () => window.clearTimeout(exitTimerId);
  }, [isHiding]);

  if (!visible && !isHiding) {
    return null;
  }

  return (
    <div
      className={cx(
        "t-stagger",
        isShown && "is-shown",
        isHiding && "is-hiding",
        className,
      )}
      role={role}
      aria-live={ariaLive}
    >
      {lines.map((line, index) => {
        const lineNumber = index + 1;
        const content = line.emphasis ? (
          <strong>{line.text}</strong>
        ) : (
          line.text
        );

        return (
          <span
            key={`${line.text}-${index}`}
            className={cx(
              "t-stagger-line",
              `t-stagger-line--${lineNumber}`,
              lineClassName,
            )}
          >
            {content}
          </span>
        );
      })}
    </div>
  );
};

export default StaggerTextReveal;
